import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form } from "react-bootstrap";
import { AdminFormPage } from "@components/CatalogIndex";
import MarkdownTextAreaV2 from "@components/FormsV2/MarkdownTextAreaV2";
import GenericToggle from "@components/FormsV2/GenericToggle/GenericToggle";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useServiceConditions, useBusinessUnitSpecialConditions } from "./api";
import type { SpecialConditionStatus } from "./api";

const FORM_ID = "terms-conditions-form";

export const TermsConditions = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { conditions, isLoading: condLoading } = useServiceConditions();
  const {
    overrides,
    isLoading: ovLoading,
    refetch,
    createOverride,
    updateOverride,
  } = useBusinessUnitSpecialConditions(id);

  const [edited, setEdited] = useState<Record<string, string>>({});
  const [skipEdits, setSkipEdits] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  if (condLoading || ovLoading) return <ScopeStackSpinner />;

  const overrideByCondition: Record<
    string,
    { id: string; content: string; status: SpecialConditionStatus }
  > = {};
  overrides.forEach((o) => {
    overrideByCondition[o.serviceConditionId] = {
      id: o.id,
      content: o.content,
      status: o.status,
    };
  });

  // Shown content/skip = the business unit's override if present, else the account
  // default (default conditions are active/included and use their own content).
  const originalContent = (conditionId: string, defaultContent: string) =>
    overrideByCondition[conditionId]?.content ?? defaultContent;
  const originalSkipped = (conditionId: string) =>
    overrideByCondition[conditionId]?.status === "skipped";

  const currentContent = (conditionId: string, defaultContent: string) =>
    edited[conditionId] ?? originalContent(conditionId, defaultContent);
  const currentSkipped = (conditionId: string) =>
    skipEdits[conditionId] ?? originalSkipped(conditionId);

  const handleSave = async () => {
    setSaving(true);
    // A condition needs an upsert if its content OR its skip status changed.
    const changed = conditions.filter((c) => {
      const contentChanged =
        edited[c.id] !== undefined &&
        edited[c.id] !== originalContent(c.id, c.content);
      const skipChanged =
        skipEdits[c.id] !== undefined &&
        skipEdits[c.id] !== originalSkipped(c.id);
      return contentChanged || skipChanged;
    });

    if (changed.length === 0) {
      setToast({ variant: "success", text: "No changes to save." });
      setSaving(false);
      return;
    }

    let failures = 0;
    for (const c of changed) {
      const content = currentContent(c.id, c.content);
      const status: SpecialConditionStatus = currentSkipped(c.id)
        ? "skipped"
        : "active";
      try {
        const existing = overrideByCondition[c.id];
        if (existing) {
          await updateOverride(existing.id, content, status);
        } else {
          await createOverride(c.id, content, status);
        }
      } catch {
        failures += 1;
      }
    }

    setToast(
      failures === 0
        ? { variant: "success", text: `Saved ${changed.length} change(s).` }
        : {
            variant: "danger",
            text: `Failed to save ${failures} of ${changed.length} change(s).`,
          }
    );
    setEdited({});
    setSkipEdits({});
    refetch();
    setSaving(false);
  };

  return (
    <>
      <AdminFormPage
        title="Terms & Conditions"
        formId={FORM_ID}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={saving}
      >
        <form
          id={FORM_ID}
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          {conditions.length === 0 ? (
            <p className="text-muted mb-0">
              No standard Terms &amp; Conditions are defined for this account.
            </p>
          ) : (
            conditions.map((c) => {
              const override = overrideByCondition[c.id];
              const skipped = currentSkipped(c.id);
              const seedKey = `${c.id}-${override?.id ?? "default"}-${
                override?.status ?? "active"
              }`;
              return (
                <Form.Group
                  key={c.id}
                  className="mb-3"
                  data-testid={`condition-${c.id}`}
                >
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="mb-0">{c.name}</Form.Label>
                    <GenericToggle
                      key={`toggle-${seedKey}`}
                      hasText
                      onText="Included"
                      offText="Excluded"
                      initialValueOfIsOn={!skipped}
                      onToggle={(isOn) =>
                        setSkipEdits((prev) => ({ ...prev, [c.id]: !isOn }))
                      }
                    />
                  </div>
                  <MarkdownTextAreaV2
                    key={`editor-${seedKey}-${skipped ? "skip" : "edit"}`}
                    value={currentContent(c.id, c.content)}
                    alwaysEdit={!skipped}
                    disabled={skipped}
                    placeholder={`Enter ${c.name}`}
                    onChange={(markdown: string) =>
                      setEdited((prev) => ({ ...prev, [c.id]: markdown }))
                    }
                  />
                </Form.Group>
              );
            })
          )}
        </form>
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default TermsConditions;
